<template>
    <div data-component="CartOverview"
         class="cart-overview"
         :class="{'edit-ref': editRef}"
         data-analytics-id="Cart Overview / Preview"
         @click.prevent="$emit('preview-clicked')">
        <div class="flex--grow">
            <span v-if="getPreviewIcon !== null"
                  class="cart-overview-preview-icon">
                <i :class="getPreviewIcon"
                   class="fa-regular"></i>
            </span>
            <div class="cart-overview-title">
                <div v-if="editRef"
                     class="input-group-flex">
                    <input id="cart-overview-staff-ref"
                           ref="ref-input"
                           v-model="stashedCart.currentCart.staffRef"
                           type="text"
                           class="form-control input-lg"
                           :placeholder="l(staffRefPlaceholder)"
                           @keydown.enter.prevent="$emit('enter-key-down')" />

                    <SquareBtn v-if="allowTableView"
                               color="info"
                               class="btn--inline-flex"
                               @click="$emit('view-tables')">
                        {{ l('ORDERS.SELECT_TABLE') }}
                    </SquareBtn>
                </div>

                <template v-else>
                    <template v-if="stashedCart.currentCart.staffRef">
                        <strong>{{ stashedCart.currentCart.staffRef }}</strong> <small>{{ time }}</small>
                    </template>
                    <template v-else-if="stashedCart.currentCart.originalOrder">
                        {{ stashedCart.currentCart.originalOrder?.originalRef }} <small>{{ time }}</small>
                    </template>
                    <template v-else>
                        {{ time }}
                    </template>
                </template>
            </div>

            <slot name="statuses"></slot>

            <div v-if="customerName"
                 class="cart-overview-detail">
                <div>
                    <i class="fa-solid fa-user"></i> {{ customerName }}
                </div>
            </div>

            <div class="cart-overview-detail">
                <div v-if="stashedCart.currentCart.originalOrder?.displayCollectionNumber">
                    <i class="fa-solid fa-hand-holding-box"></i>
                    {{ stashedCart.currentCart.originalOrder.displayCollectionNumber }}
                </div>

                <div v-if="user">
                    <i class="fa-solid fa-user-tie"></i>
                    {{ user }}
                </div>

                <div v-if="device">
                    <i class="fa-solid fa-cash-register"></i>
                    {{ device }}
                </div>

                <!-- TODO: Payment channel -->
                <!-- These aren't returned on orders loaded from the "active fulfillments" endpoint. -->
                <!--<div v-if="stashedCart.currentCart.originalOrder?.paymentChannel?.name">-->
                <!--    <i class="fa-solid fa-shopping-cart"></i>-->
                <!--    {{ stashedCart.currentCart.originalOrder.paymentChannel.name }}-->
                <!--</div>-->

                <div class="flex--no-shrink">
                    <i class="fa-solid fa-boxes"></i>
                    {{ stashedCart.totalQuantity }}
                    {{ l(stashedCart.totalQuantity === 1 ? 'ITEM' : 'ITEMS') }}
                </div>

                <div class="flex--no-shrink">
                    <i class="fa-solid fa-coins"></i>
                    {{ currencyFilter(stashedCart.totalValue) }}
                </div>
            </div>
        </div>

        <!-- Delete btn -->
        <RoundBtn v-if="showDelete"
                  color="danger"
                  icon="fa-solid fa-trash-can"
                  filled
                  :loading="stashedCart.isDeleting"
                  @click="$emit('delete-clicked')"></RoundBtn>

        <!-- Restore btn -->
        <RoundBtn v-if="showRestore"
                  color="success"
                  icon="fa-solid fa-share"
                  filled
                  :loading="stashedCart.isRestoring"
                  @click="$emit('restore-clicked')"></RoundBtn>
    </div>
</template>

<script>
import RoundBtn from '@/vue/components/RoundBtn';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';

export default {
    name: 'CartOverview',

    components: { RoundBtn, SquareBtn },

    props: {
        /**
         * @type {StashedCart}
         */
        stashedCart: {
            type: Object,
            required: true,
        },
        showDelete: {
            type: Boolean,
            default: false,
        },
        showRestore: {
            type: Boolean,
            default: false,
        },
        editRef: {
            type: Boolean,
            default: false,
        },
        staffRefPlaceholder: {
            type: String,
            default: 'ORDERS.STAFF_REF_OPTIONAL_PLACEHOLDER',
        },

        /**
         * In the MergeCartModal we can optionally preview the cart in parallel to this component.
         * Is that preview currently visible?
         */
        previewVisible: {
            type: Boolean,
            default: null,
        },

        allowTableView: {
            type: Boolean,
            default: false,
        },
    },

    emits: [
        'delete-clicked',
        'enter-key-down',
        'preview-clicked',
        'restore-clicked',
        'view-tables',
    ],

    computed: {
        user() {
            // ProMenu orders are always shown as created by the company admin user.
            // Workaround to hide the username for these.
            const subdomain = container.userService.getCompany()?.subdomain;
            if (subdomain && this.stashedCart.username === subdomain + '.admin') {
                return null;
            }

            return container.stashMetadataService.getUsername(this.stashedCart);
        },

        time() {
            return container.stashMetadataService.getRelativeTime(this.stashedCart);
        },

        device() {
            return container.stashMetadataService.getDeviceLabel(this.stashedCart);
        },

        customerName() {
            return this.stashedCart.currentCart.customer.getFullName();
        },

        /**
         * Optionally displays an icon inside the CartOverview card.
         * At the moment this specifically deals with a show/hide icon but this could easily be refactored to handle a
         * range of icon pairs if necessary.
         *
         * @return {null|string}
         */
        getPreviewIcon() {
            if (this.previewVisible === true) {
                // Lines are visible therefore the action is close.
                return 'fa-magnifying-glass-minus';
            }
            if (this.previewVisible === false) {
                // Lines are hidden therefore the action is open.
                return 'fa-magnifying-glass-plus';
            }
            return null;
        },
    },
};
</script>

<style lang="less">
@import (less, reference) "../../../../less/variables.less";
@import (less, reference) "../../../../less/mixins.less";

.cart-overview {
    border: 1px solid @btn-default-border;
    box-shadow: 0 3px 8px -9px black;
    padding: 10px;
    border-radius: @border-radius-base;
    display: flex;
    align-items: center;
    position: relative;

    .transition-all();

    &:not(.edit-ref):hover {
        background-color: @form-group-container-bg;
        cursor: pointer;
    }

    &:not(:last-child) {
        margin-bottom: @gutter-width;
    }

    .round-btn {
        width: 43px;
        height: 43px;
        margin-left: 10px;

        &:first-child {
            margin-left: 0;
            margin-right: 4px;
        }

        .fa-regular, .fa-solid {
            font-size: 18px;
        }
    }

    h4 {
        margin: 0 7px 0 0;
        font-weight: bold;
    }

    .cart-overview-title {
        display: flex;
        align-items: center;
        padding: 0 5px;
        margin-bottom: 7px;
        font-size: 16px;
        white-space: nowrap;

        .input-group-flex {
            width: 100%;
        }

        small {
            padding-left: @gutter-width;
        }
    }
    &.edit-ref {
        .cart-overview-title {
            padding: 0;
            margin-bottom: 10px;
        }
    }

    #cart-overview-staff-ref {
        font-weight: bold;
    }

    .cart-overview-detail {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        margin-bottom: -1px;

        // The customer details may be shown on an additional .cart-overview-detail line.
        &:not(:last-child) {
            margin-bottom: 5px;
        }

        i {
            color: @light-blue;
        }

        // Single piece of info inside the box. e.g. "Members", "Valid For", "Billing Period" for memberships.
        > div {
            padding: 0 6px;
            line-height: 22px;
            margin: 0 7px 0 0;
            vertical-align: middle;
            border-radius: @border-radius-base;
            font-size: 13px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            text-align: center;

            &:last-child {
                margin-right: 0;
            }
        }
    }

    .cart-overview-preview-icon {
        position: absolute;
        top:0;
        right: 2px;
        color: @dt-light-grey;
        font-size: 20px;
    }

    .items {
        ul {
            list-style: none;
            margin: 0;
            padding: 0;

            li {
                margin: 7px 0;
                font-size: 13px;
                display: flex;
                align-items: center;

                i {
                    color: @light-blue;
                }

                .item__name {
                    flex-grow: 1;
                    flex-shrink: 1;
                    overflow: hidden;
                    white-space: nowrap;
                    margin: 0 7px;
                    text-overflow: ellipsis;
                    font-size: 15px;
                    font-weight: bold;
                }

                .item__cost {
                    font-size: 15px;
                    font-weight: bold;
                }
            }
        }
    }
}
</style>
